/**
 * HVOR EN TRÅD HØRER HJEMME, OG HVORDAN MAN KOMMER DIT (QA14 #8 — gjentakelse av QA13 #12b).
 *
 * =================================================================================================
 * Atle, tredje gang: «Når en chat egentlig hører til en artist, en advance, en oppgave osv., må det
 * være en SYNLIG knapp som tar deg til stedet der chaten hører hjemme.»
 *
 * Grunnen til at den ikke fantes står i én linje i `ThreadDetailPage`:
 *
 *     if (link === null || !isThreadLinkType(link.type)) return null;
 *
 * `THREAD_LINK_TYPES` er `['contact', 'festival', 'edition']`. For `booking_bid`,
 * `booking_enquiry`, `task` og `performance` — altså for hver eneste tråd Atle klikket på — ga den
 * `null`, og siden tegnet ingenting i det hele tatt. Ikke en grå knapp, ikke en tekst: ingenting.
 *
 * =================================================================================================
 * TO ANKERE, FORDI EN DELT TRÅD ER ÉN RAD I TO HUS.
 *
 * Eieren av tråden har `link` — festivalens `booking_bid`. Gjesten kan ikke åpne den raden: den
 * ligger i et hus de ikke er medlem av. Deres eget anker er `counterpartyLink`
 * (`counterparty_linked_object_*`, 0232), og det er det som ble lagt til på DTO-en i QA14.
 *
 * `targetFor` velger gjestens anker når det finnes. Det er riktig rekkefølge: har tråden et
 * motpartsanker, ER du motparten — kolonnen settes bare for den andre siden.
 *
 * =================================================================================================
 * EN TYPE UTEN EN RUTE FÅR INGEN KNAPP, OG DET ER MED VILJE.
 *
 * `null` her betyr «vi vet ikke hvor denne fører», og da er det bedre å ikke tegne noe enn å tegne
 * en knapp som lander på en 404. Rene interne tråder har ingen lenke i det hele tatt — Atle ba
 * uttrykkelig om at de ikke skulle få en.
 */
import type { ObjectType, OrganizationKind } from '@tvitvi/shared';

/** Det knappen trenger: hvor den går, og hvilket ord den bærer. */
export interface ThreadTarget {
  /** Nøkkelen under `inbox.link.` — hver type har sin egen setning, ikke ett generisk «Åpne». */
  labelKey: string;
  /** Merkelappen i trådlista. Kortere enn knappen: den står i en rad, ikke i en header. */
  badgeKey: string;
  to: string;
  search?: Record<string, string>;
  params?: Record<string, string>;
}

/**
 * Rutene som finnes. En type som ikke står her får ingen knapp — se filhodet.
 *
 * `booking_enquiry` og `booking_bid` peker på den samme sida med hver sin søkeparameter, fordi de
 * to husene har hver sin rad for den samme samtalen (avgjørelse 185).
 */
export function targetFor(
  link: { type: ObjectType; id: string } | null,
  counterpartyLink: { type: ObjectType; id: string } | null,
  /**
   * HUSET SOM LESER, fordi forespørselen har to hjem (QA runde 5, punkt 1). `/booking` er
   * festivalens side; et byrå som fulgte den landet på arrangørens artistregister. Byråets
   * forespørsel bor på artisten deres, og `/artists?enquiry=` finner artisten og åpner dialogen.
   */
  kind: OrganizationKind = 'festival',
): ThreadTarget | null {
  /*
   * ANKERET LESEREN KAN ÅPNE (QA5 E #2). «Gjestens anker først» holdt ikke: en tråd FESTIVALEN
   * eier har byråets forespørsel som motpartsanker, og festivalen ble sendt til
   * `/booking?enquiry=<byråets id>` — en rad de ikke kan lese. Et byrå åpner ikke et bud, en
   * festival ikke en forespørsel; finnes bare det andre, får siden oversette (`?enquiry=`).
   */
  const opens = (anchor: { type: ObjectType } | null): boolean =>
    anchor !== null && anchor.type !== (kind === 'agency' ? 'booking_bid' : 'booking_enquiry');
  const chosen = [counterpartyLink, link].find(opens) ?? counterpartyLink ?? link;
  if (chosen === null) return null;

  switch (chosen.type) {
    case 'booking_enquiry':
      return {
        labelKey: 'inbox.link.booking',
        /*
         * «BOOKING» PÅ BEGGE SIDER (QA6 K3 L3). Tråden er én per artist og bærer både forespørsel
         * og bud, så festivalen så «Bud» før noe bud var sendt og byrået «Forespørsel» om et bud.
         * Merket kan ikke vite hvilken rad tråden handler om nå; ordet som er sant for begge er det.
         */
        badgeKey: 'inbox.linkBadge.booking',
        ...enquiryDestination(chosen.id, kind),
      };
    case 'booking_bid':
      /* Budet er festivalens rad; et byrå kan ikke åpne den, og da er ingen knapp bedre enn en feil. */
      if (kind === 'agency') return null;
      return {
        labelKey: 'inbox.link.booking',
        badgeKey: 'inbox.linkBadge.booking',
        to: '/booking',
        search: { bid: chosen.id },
      };
    case 'booking_artist':
      return {
        labelKey: 'inbox.link.artist',
        badgeKey: 'inbox.linkBadge.artist',
        to: '/artists/$artistId',
        params: { artistId: chosen.id },
      };
    case 'task':
      return {
        labelKey: 'inbox.link.task',
        badgeKey: 'inbox.linkBadge.task',
        /* `open`, som oppgavesida leser — `?task=` åpnet ingenting (QA5-B). */
        to: '/tasks',
        search: { open: chosen.id },
      };
    case 'contact':
      return {
        labelKey: 'inbox.link.contact',
        badgeKey: 'inbox.linkBadge.contact',
        /* Kontaktens egen side; `/contacts?contact=` var hele lista, uten noe åpnet (QA5-B). */
        to: '/contacts/$contactId',
        params: { contactId: chosen.id },
      };
    default:
      /*
       * `festival`, `edition`, `performance`, `document` og resten: tråden bærer dem, men det
       * finnes ingen adresse som åpner NØYAKTIG den raden ennå. En knapp til oversikten ville
       * vært en knapp som lover mer enn den holder.
       */
      return null;
  }
}

/**
 * Hvor en forespørsel åpnes, for huset som leser. Delt med varselklokka: de to lenket hver for seg
 * til `/booking`, og begge sendte byrået til arrangørens side.
 */
export function enquiryDestination(
  enquiryId: string,
  kind: OrganizationKind,
): { to: string; search: Record<string, string> } {
  return kind === 'agency'
    ? { to: '/artists', search: { enquiry: enquiryId } }
    : { to: '/booking', search: { enquiry: enquiryId } };
}
